import React, { createContext, useContext, useMemo } from "react";
import {
  type FormAppearance,
  type FormTheme,
  DEFAULT_FORM_THEME,
  resolveFormTheme,
  formThemeCssVars,
} from "@eaco/forms-core";

/**
 * Provides the resolved form theme to everything inside a `<DocumentView>`
 * (or an editor preview). It (a) sets CSS variables on a wrapper so buttons /
 * links / selection states can read `var(--forms-accent)` etc., and (b)
 * exposes the resolved `FormTheme` via context for components that need the
 * values in JS. Both the live fill (FillFormBody) and the Appearance /
 * Submission previews wrap in this, so preview == fill by construction.
 */
const FormThemeContext = createContext<FormTheme>(DEFAULT_FORM_THEME);

export const useFormTheme = (): FormTheme => useContext(FormThemeContext);

interface FormThemeProviderProps {
  /** The form's persisted appearance, or null for the platform default. */
  appearance?: FormAppearance | null;
  /** When true, don't render a wrapper element (context-only). */
  contextOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const FormThemeProvider: React.FC<FormThemeProviderProps> = ({
  appearance,
  contextOnly = false,
  className,
  style,
  children,
}) => {
  const theme = useMemo(() => resolveFormTheme(appearance), [appearance]);
  const vars = useMemo(() => formThemeCssVars(theme), [theme]);

  return (
    <FormThemeContext.Provider value={theme}>
      {contextOnly ? (
        children
      ) : (
        <div
          className={className}
          style={{ ...(vars as React.CSSProperties), ...style }}
        >
          {children}
        </div>
      )}
    </FormThemeContext.Provider>
  );
};

export default FormThemeProvider;
